import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Alert, Box, Card, CircularProgress } from '@mui/material';
import packages, { apiError, packageStatus } from 'common/packages';
import NotPackageManager, { usePackageManager } from 'components/NotPackageManager';
import { PackageTable, TableToolbar } from './components';

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const compare = (a, b, orderBy) => {
  switch (orderBy) {
    case 'size':
      return Number(a.size || 0) - Number(b.size || 0);
    case 'updated_at':
      return new Date(a.updated_at || 0) - new Date(b.updated_at || 0);
    case 'status':
      return collator.compare(packageStatus(a), packageStatus(b));
    default:
      return collator.compare(a[orderBy] || '', b[orderBy] || '');
  }
};

const PackageList = () => {
  const { application } = useParams();
  const { isManager, error: permissionError } = usePackageManager();

  const [applications, setApplications] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sort, setSort] = useState({ by: 'updated_at', direction: 'desc' });

  useEffect(() => {
    if (isManager !== true) return undefined;
    let active = true;
    packages
      .applications()
      .then((list) => {
        if (active) setApplications(list);
      })
      .catch((e) => {
        if (active) {
          setApplications([]);
          setError(apiError(e));
        }
      });
    return () => {
      active = false;
    };
  }, [isManager]);

  useEffect(() => {
    if (isManager !== true || !application) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    setPage(0);
    packages
      .listManaged(application)
      .then((data) => {
        if (active) setRows((data && data.packages) || []);
      })
      .catch((e) => {
        if (active) {
          setRows([]);
          setError(apiError(e));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isManager, application]);

  // type is free text server-side, so the filter offers what the response
  // actually contains rather than a vocabulary that would go stale.
  const types = useMemo(() => {
    const present = rows.map((pkg) => pkg.type).filter(Boolean);
    return Array.from(new Set(present)).sort((a, b) => collator.compare(a, b));
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = rows.filter((pkg) => {
      if (type && pkg.type !== type) return false;
      if (status !== 'All' && packageStatus(pkg) !== status) return false;
      if (!term) return true;
      return [pkg.name, pkg.version, pkg.label].some(
        (field) => field && String(field).toLowerCase().includes(term)
      );
    });
    const direction = sort.direction === 'asc' ? 1 : -1;
    return matches.sort((a, b) => direction * compare(a, b, sort.by));
  }, [rows, search, type, status, sort]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const onFilterChange = (setter) => (value) => {
    setter(value);
    setPage(0);
  };

  if (permissionError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Could not check package-management permissions: {permissionError.message}
        </Alert>
      </Box>
    );
  }

  if (isManager === null) {
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isManager === false) {
    return (
      <Box sx={{ p: 4 }}>
        <NotPackageManager />
      </Box>
    );
  }

  // /packages has no application segment: send it to the first one, so the
  // selected application always lives in the URL and stays shareable.
  if (!application) {
    if (applications === null) {
      return (
        <Box sx={{ p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (applications.length === 0) {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="warning">
            No licensed applications are available to manage.
            {error ? ` (${error.message})` : ''}
          </Alert>
        </Box>
      );
    }
    return <Navigate replace to={`/packages/${applications[0].code}`} />;
  }

  return (
    <Box sx={{ p: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      <Card>
        <TableToolbar
          application={application}
          applications={applications || []}
          search={search}
          onSearchChange={onFilterChange(setSearch)}
          type={type}
          types={types}
          onTypeChange={onFilterChange(setType)}
          status={status}
          onStatusChange={onFilterChange(setStatus)}
          total={filtered.length}
        />
        <PackageTable
          application={application}
          loading={loading}
          packages={paged}
          total={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          sort={sort}
          onSortChange={setSort}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(0);
          }}
        />
      </Card>
    </Box>
  );
};

export default PackageList;
